import { FC, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { InView } from 'react-intersection-observer'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { TheatricalExpressPerkViewer } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressPerkViewer'
import { TheatricalExpressPriceOptions } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressPriceOptions'
import { TheatricalPifCheckoutWithSubtotal } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalPifCheckoutWithSubtotal'

interface TheatricalPifInlineOffersProps {
  theatricalRelease: TheatricalReleaseResult
  showOffers: boolean
  shouldShowPeopleImpacted?: boolean
  shouldUseEqualColumns?: boolean
  earlyAccessMinimumPrice?: number
  newPifLayout?: boolean
  pifNameInCreditsEnabled?: boolean
  washingtonOfferExperiment?: boolean
}

export const TheatricalPifInlineOffers: FC<TheatricalPifInlineOffersProps> = ({
  theatricalRelease,
  showOffers,
  shouldShowPeopleImpacted = false,
  shouldUseEqualColumns = false,
  earlyAccessMinimumPrice,
  newPifLayout,
  pifNameInCreditsEnabled,
  washingtonOfferExperiment = false,
}) => {
  const { offersData, selectedOffer, handleSetSelectedOffer, handleSetSelectedOfferId } = usePayItForwardOfferData()
  const { query, isReady } = useSafeRouter()
  const { trackProductViewed } = useEcommerceEvents()

  useEffect(() => {
    if (isReady && !!query?.defaultOffer) {
      handleSetSelectedOfferId(query?.defaultOffer as string)
    }
  }, [handleSetSelectedOffer, handleSetSelectedOfferId, isReady, query?.defaultOffer])

  if (!offersData || !selectedOffer) return null

  return (
    <AnimatePresence>
      {showOffers && (
        <motion.div
          initial={{ opacity: 0, y: -20, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: -20, height: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <InView
            id="pay-it-forward-inline-offers"
            as="section"
            className="w-full overflow-hidden"
            triggerOnce
            onChange={(inView) => {
              if (!inView) return

              trackProductViewed({
                funnel: 'pif',
                product_id: selectedOffer.id,
                sku: selectedOffer.slug,
                category: 'pif',
                name: selectedOffer.name,
                price: getPriceForCurrency(selectedOffer.price, selectedOffer.currency),
                currency: selectedOffer.currency as MoneyCurrencyCode,
                quantity: 1,
                projectSlug: theatricalRelease?.theatricalSlug,
              })
            }}
          >
            <TheatricalExpressPriceOptions
              shouldUseEqualColumns={shouldUseEqualColumns}
              shouldShowPeopleImpacted={shouldShowPeopleImpacted}
              offers={offersData}
              onClick={handleSetSelectedOffer}
              selectedOffer={selectedOffer}
              pifNameInCreditsEnabled={pifNameInCreditsEnabled}
              washingtonOfferExperiment={washingtonOfferExperiment}
            />
            <TheatricalExpressPerkViewer
              earlyAccessMinimumPrice={earlyAccessMinimumPrice}
              shouldShowDedicationExperiment={false}
              selectedOffer={selectedOffer}
              theatricalSlug={theatricalRelease?.theatricalSlug}
              projectName={theatricalRelease?.title as string}
              isExpressCheckout={false}
              newPifLayout={newPifLayout}
              pifNameInCreditsEnabled={pifNameInCreditsEnabled}
            />
            <TheatricalPifCheckoutWithSubtotal
              selectedOffer={selectedOffer}
              projectSlug={theatricalRelease?.theatricalSlug}
              isSubscription={false}
              newPifLayout={newPifLayout}
              pifNameInCreditsEnabled={pifNameInCreditsEnabled}
            />
          </InView>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
